import PaginationTable, {
    PaginationTableProps,
} from '../../../../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import { TiktokAudiosTableRowV2 } from '../../../../../../TikTok.types';
import React, { useCallback, useMemo } from 'react';
import { GenericDropdownOption } from '../../../../../../../../App.types';
import { StylesConfig } from 'react-select';
import { formatNumberToKNotation, roundTo2Dp } from '../../../../../../../../helpers';
import { Column } from 'react-table';
import cn from 'classnames';
import styles from './TiktokCollectionsAudiosTable.module.css';
import Skeleton from '../../../../../../../../ui/Skeleton/Skeleton';
import TruncatedTextCell from '../../../../../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import BarsSmallChart from '../../../../../../../../ui/DataDisplay/Charts/BarsSmallChart/BarsSmallChart';
import SearchInput from '../../../../../../../../ui/DataEntry/SearchInput/SearchInput';
import Select from '../../../../../../../../ui/DataEntry/Select/Select';
import { GetTiktokAudioCollectionAudioStatsParams } from '@round/api';
import TiktokAudioBadge from '../../../../../components/TiktokAudioBadge/TiktokAudioBadge';

type TiktokCollectionsAudiosTableProps = Omit<
    PaginationTableProps<TiktokAudiosTableRowV2>,
    'columns' | 'orderBy' | 'onOrderByChange'
> & {
    ordering: GetTiktokAudioCollectionAudioStatsParams['ordering'];
    setOrdering: (key: GetTiktokAudioCollectionAudioStatsParams['ordering']) => void;
    search: string;
    setSearch: (query: string) => void;
    renderToolbarRight?: () => React.ReactNode;
    renderToolbarLeft?: () => React.ReactNode;
};

const sortingOptions: GenericDropdownOption<GetTiktokAudioCollectionAudioStatsParams['ordering']>[] = [
    { label: 'Total Videos', value: '-video_count' },
    { label: 'Daily Growth (# videos)', value: '-video_count_daily_change' },
    { label: 'Daily Growth (% uplift)', value: '-video_count_daily_change_relative' },
    { label: 'Weekly Growth (# videos)', value: '-video_count_weekly_change' },
    { label: 'Weekly Growth (% uplift)', value: '-video_count_weekly_change_relative' },
];

const sortingSelectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#C2CFE0',
    }),
    valueContainer: (base) => ({
        ...base,
        minWidth: '10rem',
    }),
    singleValue: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
    menuList: (base) => ({
        ...base,
        color: '#58698E',
        fontWeight: 600,
        fontSize: '0.75rem',
    }),
};

const isValidKey = (value: string | undefined): value is keyof TiktokAudiosTableRowV2 =>
    (value !== undefined && value === 'video_count') ||
    value === 'video_count_daily_change' ||
    value === 'video_count_daily_change_relative' ||
    value === 'video_count_weekly_change' ||
    value === 'video_count_weekly_change_relative' ||
    value === 'alternate_name' ||
    value === 'author_name' ||
    value === 'title' ||
    value === 'is_original' ||
    value === 'tiktok_id' ||
    value === 'image_url' ||
    value === 'audioUrl' ||
    value === 'clientMonitoredAudioRelation';

const TiktokCollectionsAudiosTable = ({
    pageSize,
    setPageSize,
    setPage,
    page,
    loading,
    data,
    noDataLabel,
    count,
    onRowClick,
    ordering,
    setOrdering,
    search,
    setSearch,
    renderToolbarRight,
    renderToolbarLeft,
    onPaginationChange,
}: TiktokCollectionsAudiosTableProps) => {
    const sortingValue = sortingOptions.find((option) => option.value === ordering);
    const formatVideoCountBadgeValue = useCallback(
        (n: number) => {
            if (sortingValue?.value?.includes('relative')) {
                return `${roundTo2Dp(n * 100)}%`;
            }

            return formatNumberToKNotation(n);
        },
        [sortingValue?.value]
    );

    const columns: Column<TiktokAudiosTableRowV2>[] = useMemo(
        () => [
            {
                Header: 'Sound',
                accessor: 'title',
                Cell: ({ row: { original } }) => (
                    <TiktokAudioBadge
                        loading={loading}
                        audio={{
                            tiktokId: original.tiktok_id,
                            clientMonitoredAudioRelationships: original.clientMonitoredAudioRelation,
                            imageUrl: original.image_url,
                            audioPlayUrl: original.audioUrl,
                            title: original.title,
                            isOriginal: original.is_original,
                            trackingType: original.tracking_type,
                        }}
                    />
                ),
            },
            {
                Header: 'Author',
                accessor: 'author_name',
                Cell: ({ value }) =>
                    loading ? (
                        <Skeleton width="75%" />
                    ) : (
                        <TruncatedTextCell className={styles.authorName}>{value}</TruncatedTextCell>
                    ),
            },
            {
                Header: 'Video',
                accessor: 'video_count',
                Cell: ({ value, row: { original } }) => {
                    if (loading) {
                        return <Skeleton width="75%" />;
                    }

                    const orderByValue =
                        sortingValue?.value?.charAt(0) === '-' ? sortingValue?.value.slice(1) : sortingValue?.value;
                    const badgeValue =
                        orderByValue && isValidKey(orderByValue) && orderByValue !== 'video_count'
                            ? original[orderByValue]
                            : null;

                    return (
                        <>
                            <span className={styles.videoCount}>{formatNumberToKNotation(value)}</span>
                            {typeof badgeValue === 'number' && (
                                <span
                                    className={cn(styles.growthBadge, {
                                        [styles.increasing]: badgeValue >= 0,
                                        [styles.decreasing]: badgeValue < 0,
                                    })}
                                >
                                    {badgeValue > 0 && '+'}
                                    {formatVideoCountBadgeValue(badgeValue)}
                                </span>
                            )}
                        </>
                    );
                },
            },
            {
                Header: 'Growth',
                accessor: 'dailyGrowth',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.dailyGrowth}>
                            <BarsSmallChart values={value} color="linear-gradient(180deg, #93AAFF 0%, #6BC1FF 100%)" />
                        </div>
                    );
                },
            },
        ],
        [formatVideoCountBadgeValue, loading, sortingValue]
    );

    return (
        <>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <SearchInput className={styles.searchInput} value={search} onChange={setSearch} />
                    {typeof renderToolbarLeft === 'function' && renderToolbarLeft()}
                </div>

                <div className={styles.headerRight}>
                    <label className={styles.label}>Sort by</label>
                    <div data-test-id="sort-container">
                        <Select
                            isSearchable={false}
                            styles={sortingSelectStyles}
                            value={sortingValue}
                            options={sortingOptions}
                            onChange={(option) => setOrdering(option?.value)}
                        />
                    </div>

                    {typeof renderToolbarRight === 'function' && renderToolbarRight()}
                </div>
            </div>
            <div data-test-id="audios-table">
                <PaginationTable
                    tableClassName={styles.table}
                    loading={loading}
                    data={data}
                    noDataLabel={noDataLabel}
                    columns={columns}
                    count={count}
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    onRowClick={onRowClick}
                    onPaginationChange={onPaginationChange}
                />
            </div>
        </>
    );
};

export default TiktokCollectionsAudiosTable;
